import moment from "moment";
import {
    DEFAULT_EMBEDD_SETTINGS,
    DEFAULT_WEBCHAT_COLORS,
    DEFAULT_WEBCHAT_THEME,
    BottomMenuSettings,
    IntroEventCard,
    WebChatBannerSettings,
    WebChatEmbeddedSettings,
    WebChatThemeProps
} from "@42maru/webchat";
import { INVALID_CODES } from "@/core/validation";
import { ParagraphButtonData, ValidationResult } from "@/model";
import { isUrl } from "@/util/stringUtil";
import koDummy from "@/assets/json/chat-dummy-ko.json";
import enDummy from "@/assets/json/chat-dummy-en.json";


export const BUTTON_TEXT_MAX_LENGTH = 16;
export const TITLE_MAX_LENGTH = 30;
export const INTRO_TITLE_MAX_LENGTH = 30;
export const INTRO_SUBTITLE_MAX_LENGTH = 30;
export const AUTO_COMPLETE_MAX_LIMIT = 10;
const EVENT_CARD_DATE_MIN_LENGTH = 18;
const BUTTON_ICON_PADDING_RATIO = 0.06;
const EVENT_CARD_DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";

export const DEFAULT_BUTTON_ICON = DEFAULT_WEBCHAT_THEME.embeddedSettings?.buttonIcon;

export const mergeThemeWithDefault = (theme?: WebChatThemeProps): WebChatThemeProps => {
    const themeClone = structuredClone(theme);

    return {
        ...DEFAULT_WEBCHAT_THEME,
        ...themeClone,
        titleImageUrl: DEFAULT_WEBCHAT_THEME.titleImageUrl,
        colors: {
            ...DEFAULT_WEBCHAT_COLORS,
            ...themeClone?.colors,
            chatTitleTextColor: DEFAULT_WEBCHAT_COLORS.chatTitleTextColor,
        },
        embeddedSettings: {
            ...DEFAULT_EMBEDD_SETTINGS,
            ...themeClone?.embeddedSettings,
        },
    } as WebChatThemeProps;
};

const calculateAvatarSize = (buttonWidth: number, buttonIcon?: string): number => {
    if (buttonIcon && buttonIcon !== DEFAULT_BUTTON_ICON) {
        return buttonWidth - 2 * Math.floor(buttonWidth * BUTTON_ICON_PADDING_RATIO);
    }
    return Math.floor(buttonWidth / 2);
};

const refineEmbeddedButtonSettings = (
    settings: WebChatEmbeddedSettings,
    buttonWidth?: number
): void => {
    if (!buttonWidth) {
        return;
    }
    const avatarSize = calculateAvatarSize(buttonWidth, settings.buttonIcon);
    settings.buttonHeight = buttonWidth;
    settings.buttonIconWidth = avatarSize;
    settings.buttonIconHeight = avatarSize;
};

export const refineBannerSettings = (settings: WebChatBannerSettings): WebChatBannerSettings => {
    const settingsClone = structuredClone(settings);
    refineEmbeddedButtonSettings(settingsClone, settingsClone.buttonWidth);
    if (settingsClone.mobile?.buttonWidth) {
        refineEmbeddedButtonSettings(settingsClone.mobile, settingsClone.mobile.buttonWidth);
    }
    return settingsClone;
};

const parsedEnDummy = JSON.parse(JSON.stringify(enDummy));
const parsedKoDummy = JSON.parse(JSON.stringify(koDummy));
export const previewDummyMessages = (language?: string) => {
    return language === "en" ? parsedEnDummy : parsedKoDummy;
};

export const isValidTitle = (value: string): ValidationResult => {
    if (!value) {
        return { isValid: false, code: INVALID_CODES.REQUIRED };
    }
    if (value.length > TITLE_MAX_LENGTH) {
        return { isValid: false, code: INVALID_CODES.MAX };
    }
    return { isValid: true };
};

const ALIAS_REGEX = /^[A-Za-z0-9-]+$/;
export const isValidAlias = (value?: string): ValidationResult => {
    if (!value) {
        return { isValid: true };
    }
    if (!ALIAS_REGEX.test(value)) {
        return { isValid: false, code: INVALID_CODES.INVALID_FORMAT };
    }
    return { isValid: true };
};

export const isValidIntroTitleText = (value: string): ValidationResult => {
    if (value.length > INTRO_TITLE_MAX_LENGTH) {
        return { isValid: false, code: INVALID_CODES.MAX };
    }
    return { isValid: true };
};

export const isValidIntroSubtitleText = (value?: string): ValidationResult => {
    if (!value) {
        return { isValid: true };
    }
    if (value.length > INTRO_SUBTITLE_MAX_LENGTH) {
        return { isValid: false, code: INVALID_CODES.MAX };
    }
    return { isValid: true };
};

export const isValidAutoCompleteLimit = (value?: number): ValidationResult => {
    if (value === undefined) {
        return { isValid: false, code: INVALID_CODES.REQUIRED };
    }
    if (value < 0) {
        return { isValid: false, code: INVALID_CODES.MIN };
    }
    if (value > AUTO_COMPLETE_MAX_LIMIT) {
        return { isValid: false, code: INVALID_CODES.MAX };
    }
    return { isValid: true };
};

export const isValidBannerText = (value?: string): ValidationResult => {
    if (value && value.length > BUTTON_TEXT_MAX_LENGTH) {
        return { isValid: false, code: INVALID_CODES.MAX };
    }
    return { isValid: true };
};

const isValidUrlList = (list: string[]): ValidationResult => {
    if (!list.length) {
        return { isValid: true };
    }
    if (list.some(url => !url.trim() || !isUrl(url))) {
        return { isValid: false, code: INVALID_CODES.INVALID_FORMAT };
    }
    if (new Set(list).size !== list.length) {
        return { isValid: false, code: INVALID_CODES.DUPLICATE };
    }
    return { isValid: true };
};

export const isValidBannerTextWhiteList = (joinedList?: string): ValidationResult => {
    const list = joinedList?.split(",").filter(url => url.trim() !== "") ?? [];
    return isValidUrlList(list);
};

const isValidEmbeddedSettings = (settings: WebChatEmbeddedSettings): ValidationResult => {
    return {
        isValid: (
            isValidBannerText(settings.bannerText).isValid
            && isValidBannerTextWhiteList(settings.buttonShowUrlWhiteList).isValid
        )
    };
};
export const isValidBannerSettings = (settings: WebChatBannerSettings): ValidationResult => {
    return {
        isValid: (
            isValidEmbeddedSettings(settings).isValid
            && (!settings.mobile || isValidEmbeddedSettings(settings.mobile).isValid)
        )
    };
};

const isValidPayload = (prefix: string, payload?: string): boolean => {
    if (!payload) {
        return false;
    }
    return payload.startsWith(prefix) && payload.replace(prefix, "").trim() !== "";
};

export const isValidButtonData = (data: ParagraphButtonData): boolean => {
    if (!data?.button_type) {
        return false;
    }
    switch (data.button_type) {
        case "text":
            return isValidPayload("text:", data.payload);
        case "next":
            return isValidPayload("custom_id:", data.payload);
        case "link":
            if (!data.link_mobile || !isUrl(data.link_mobile)) {
                return false;
            }
            if (data.link_pc && !isUrl(data.link_pc)) {
                return false;
            }
            return true;
        case "scenario":
            return isValidPayload("act_scenario:", data.payload);
        default:
            return false;
    }
};

export const isValidBottomMenu = (menu?: BottomMenuSettings): ValidationResult => {
    if (!menu?.data || !menu.enable) {
        return { isValid: true };
    }
    return { isValid: menu.data.every(isValidButtonData) };
};


const isValidEventCardDate = (date: string): boolean => {
    if (date.length < EVENT_CARD_DATE_MIN_LENGTH) {
        return false;
    }
    return moment(date, EVENT_CARD_DATE_FORMAT, true).isValid();
};
export const isValidEventCards = (eventCards?: IntroEventCard[]): ValidationResult => {
    if (!eventCards?.length) {
        return { isValid: true };
    }
    return {
        isValid: eventCards?.every(({ startDate, endDate }) =>
            (!startDate || isValidEventCardDate(startDate))
            && (!endDate || isValidEventCardDate(endDate))
        )
    };
};
